// CalmYourBodyPanel.tsx — the on-brand front-screen flow that replaces the old
// "how are you arriving / Regulate now" banner. An in-place tile state machine:
//
//   choice → (emotions → manageable → quickReset | breathing)
//          → (relaxation → breathing)
//          → (rebuild → collapsed re-entry tile)
//   returnChoice (shown after a breathing session) → emotions | collapsed
//
// Breathing itself is the ported CalmBreathing overlay, opened via onStartBreathing
// (it lives in App's overlay slot so it pins to the viewport). When breathing is
// launched the panel pre-sets its step to 'returnChoice', so closing the overlay
// reveals the "welcome back" choices underneath.
import React from 'react';
import { createPortal } from 'react-dom';
import { Display, Body, Pressable } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';
import { BackButton } from '@/components/BackButton';
import { useProgress } from '@/hooks/useProgress';
import { STATES } from '@/data/calm/states';
import { protocolForState } from '@/data/calm/protocols';
import { techniquesForState, type Technique } from '@/data/calm/quickResets';
import { clipUrl, GUIDE_MESSAGES } from '@/data/guideMessages';
import { entriesForTag } from '@/services/library';
import { CARE_TAGLINE } from '@/data/care';
import { useGuide } from '@/flows/guide/useGuide';
import { GuidePresence } from '@/flows/guide/GuidePresence';
import type { GuideSelection } from '@/services/guidePresence';

const RELAX_PROTOCOL = 'relax_box';
const EMOTION_STATES = STATES.filter((s) => s.id !== 'relax');
const shortLabel = (label: string) => label.split('/')[0].trim();

// The full-screen redesign of the emotions/manageable steps (FullScreenShell,
// below) was NATIVE-ONLY at first; as of 2026-09-22 it ships on web too. The old
// inline-Tile branch it gates against is now dead in practice but left in place
// (this is a flag flip, not a dead-code removal pass) — this file will eventually
// shed that branch along with this const.
const CALM_FULLSCREEN = true;

// Plain, accessible labels for the pills (two words where it helps clarity).
const STATE_LABEL: Record<string, string> = {
  panic: 'Panic',
  overwhelm: 'Overwhelmed',
  edgy: 'Stressed / anxious',
  rumination: 'Looping thoughts',
  cant_sleep: "Can't sleep",
  dissociated: 'Numb',
  flat: 'Flat / empty',
};

// On-brand line icons for each emotional state (replaces the source emojis).
const STATE_ICON: Record<string, IconName> = {
  panic: 'emoPanic',
  overwhelm: 'emoOverwhelm',
  edgy: 'emoEdgy',
  rumination: 'emoRumination',
  cant_sleep: 'moon',
  dissociated: 'emoNumb',
  flat: 'emoFlat',
};

type Step = 'choice' | 'emotions' | 'manageable' | 'quickReset' | 'quickResetDone' | 'exHub';

// When opening a Library reading theme from the ex screen switches tabs (which
// unmounts Today), this restores the screen the user left so back returns to the ex
// screen, not a reset arrival card. One-shot: read and cleared on the next mount.
let resumeArrivalStep: Step | null = null;

/** The arrival's "Calm your body" action-row gradient (ArrivalHeader). The steps this
 *  button reveals reuse it so the panel visually continues the row that was tapped. */
const CALM_PLANE = 'linear-gradient(158deg,#d9e7e8,#c6dadd)';

function Tile({ children, tone = 'default' }: { children: React.ReactNode; tone?: 'default' | 'calm' }) {
  return (
    <div
      style={{
        // the calm container plane the lifted option cards sit on (resting)
        background: tone === 'calm' ? CALM_PLANE : 'var(--card-grad)',
        border: '1px solid var(--hair)',
        borderRadius: 18,
        boxShadow: 'var(--edge-light), var(--lift-soft)',
        padding: '16px 16px 17px',
        animation: 'fade .3s ease',
      }}
    >
      {children}
    </div>
  );
}

function Eyebrow({ children, onBack }: { children: React.ReactNode; onBack?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 30,
            height: 30,
            borderRadius: 999,
            border: '1px solid var(--hair)',
            background: 'var(--card-top)',
            color: 'var(--ink-soft)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="chevLeft" size={16} />
        </button>
      )}
      <span
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--amber-deep)',
        }}
      >
        {children}
      </span>
    </div>
  );
}

function OptionRow({
  icon,
  title,
  sub,
  tag,
  tone = 'amber',
  onClick,
}: {
  icon: IconName;
  title: string;
  sub?: string;
  /** Optional small pill beside the title (e.g. "In the moment"). */
  tag?: string;
  tone?: 'amber' | 'green';
  onClick: () => void;
}) {
  const color = tone === 'green' ? 'var(--green-deep)' : 'var(--amber-deep)';
  // Soft accent-tinted icon tile (replaces the dark recessed --well tan, which read
  // as muddy). Gold for the in-the-moment rows, sage-green for self-compassion.
  const tileBg = tone === 'green' ? 'rgba(29,158,117,0.12)' : 'rgba(200,134,26,0.14)';
  const tileRing = tone === 'green' ? 'rgba(29,158,117,0.22)' : 'rgba(200,134,26,0.24)';
  return (
    <Pressable
      as="button"
      onClick={onClick}
      ariaLabel={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        padding: '12px 13px',
        borderRadius: 14,
        border: '1px solid var(--hair)',
        // lifted option card
        background: 'var(--card-raised-grad)',
        boxShadow: 'var(--edge-light), var(--lift-soft)',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // accent-tinted icon tile (was the dark recessed --well)
          background: tileBg,
          boxShadow: `inset 0 0 0 1px ${tileRing}`,
          color,
        }}
      >
        <Icon name={icon} size={19} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, fontWeight: 500, color: 'var(--ink)' }}>
          {title}
          {tag && (
            <span
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#9C3D2C',
                background: 'rgba(161,75,64,0.10)',
                border: '1px solid rgba(161,75,64,0.22)',
                borderRadius: 999,
                padding: '2px 8px',
              }}
            >
              {tag}
            </span>
          )}
        </span>
        {sub && (
          <span style={{ display: 'block', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 1 }}>
            {sub}
          </span>
        )}
      </span>
      <span style={{ color: 'var(--ink-soft)', opacity: 0.4, flexShrink: 0 }}>
        <Icon name="chevRight" size={16} />
      </span>
    </Pressable>
  );
}

// A small uppercase section divider inside the ex-difficulty screen.
function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--amber-deep)',
        margin: '18px 0 10px',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Full-screen shell for the emotions and manageable steps (see CALM_FULLSCREEN
// above) — same top-bar pattern as ExerciseViewer/
// CareReflection: a circular back button (contextual, goes to the previous step), a
// small eyebrow title, and a circular close button that always exits the whole Calm
// Your Body flow (mirrors the exit affordance CalmBreathing gives via its own
// Back-to-exit control).
//
// CalmYourBodyPanel is mounted inline deep in HomeScreen's scrollable content (in
// three different places), not in App's overlay slot, so a plain `position:
// absolute` here wouldn't have a correctly-sized positioned ancestor. Instead this
// portals into #rn-overlay-root — the SAME position:relative, width-constrained
// container CareReflection/ExerciseViewer render into as AppShell `overlays`
// children (see AppShell.tsx) — and uses `position: absolute` there, same
// containment contract as every other full-screen flow (never `position: fixed`,
// which would escape that column and cover the desktop side nav). Falls back to an
// inline, unportalled render if the root isn't in the DOM yet (e.g. isolated tests).
function FullScreenShell({
  title,
  onBack,
  onClose,
  standardBack = false,
  children,
}: {
  title: string;
  onBack: () => void;
  onClose: () => void;
  standardBack?: boolean;
  children: React.ReactNode;
}) {
  const [portalRoot, setPortalRoot] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setPortalRoot(document.getElementById('rn-overlay-root'));
  }, []);

  const shell = (
    <div
      role="dialog"
      aria-label={title}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 84,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'calc(14px + var(--safe-top)) 18px 12px', background: 'var(--cream)' }}>
        {standardBack ? <BackButton onClick={onBack} /> : <Pressable
          as="button"
          onClick={onBack}
          ariaLabel="Back"
          style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: 'var(--edge), var(--sh1)' }}
        >
          <Icon name="chevLeft" size={19} />
        </Pressable>}
        <span
          style={{
            flex: 1,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--amber-deep)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </span>
        {!standardBack && <Pressable
          as="button"
          onClick={onClose}
          ariaLabel="Close"
          style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: 'var(--edge), var(--sh1)' }}
        >
          <Icon name="chevDown" size={19} />
        </Pressable>}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '4px 18px calc(28px + var(--safe-bottom))' }}>
        {children}
      </div>
    </div>
  );

  return portalRoot ? createPortal(shell, portalRoot) : shell;
}

// A reading-category row (visibly a different category to the tools): a coloured
// motif tile, the theme name, and a live article count in the theme's colour.
function LibRow({
  title,
  count,
  accent,
  onClick,
}: {
  title: string;
  count: number;
  accent: string;
  onClick: () => void;
}) {
  return (
    <Pressable
      as="button"
      onClick={onClick}
      ariaLabel={`${title} — ${count} articles`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        width: '100%',
        textAlign: 'left',
        padding: '11px 12px',
        borderRadius: 14,
        border: '1px solid var(--hair)',
        background: 'var(--card-top)',
        cursor: 'pointer',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 50,
          height: 50,
          borderRadius: 13,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          background: `radial-gradient(46px 46px at 30% 25%, rgba(255,255,255,0.35), transparent 70%), linear-gradient(150deg, ${accent}, ${accent})`,
        }}
      >
        <Icon name="book" size={22} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 17, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.1 }}>
          {title}
        </span>
        <span style={{ display: 'block', marginTop: 3, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600, color: accent }}>
          {count} {count === 1 ? 'article' : 'articles'}
        </span>
      </span>
      <span style={{ color: 'var(--ink-soft)', opacity: 0.4, flexShrink: 0, display: 'flex' }}>
        <Icon name="chevRight" size={16} />
      </span>
    </Pressable>
  );
}

export function CalmYourBodyPanel({
  onStartBreathing,
  onOpenCrisis,
  onOpenTool,
  onOpenLibraryTheme,
  onOpenCare,
  collapsed,
  onCollapse,
  onExpand,
  locked = false,
  featureLock = false,
  onLocked,
  initialStep,
  newUi = false,
  standardBack = false,
  onStepChange,
}: {
  onStartBreathing: (protocolId: string, careEligible?: boolean) => void;
  onOpenCrisis: () => void;
  /** Open the CARE reflection overlay directly from the quick-reset completion. */
  onOpenCare?: () => void;
  /** Open the panel straight onto a given step (e.g. 'emotions' from the new
      Today header's "Settle the body"), bypassing the top-level choice. */
  initialStep?: Step;
  /** New Today context: hide the in-flow guide tile, lay the emotions out as a tidy
      grid, and make Back from the (root) emotions step CLOSE instead of dropping to
      the old "what do you need" choice header (removed from the new design). */
  newUi?: boolean;
  /** TRIAGE_V2: the full-screen steps use the one standard Back and drop the separate close. */
  standardBack?: boolean;
  /** Called on each internal step change so the host can re-anchor it to the top. */
  onStepChange?: () => void;
  /** Open a toolkit exercise by id (the "Difficulty with my ex" tools). */
  onOpenTool: (toolId: string) => void;
  /** Open the Library filtered to a theme/category key (e.g. 'Hold', 'Co-parenting'). */
  onOpenLibraryTheme: (theme: string) => void;
  /** Open/collapsed is owned by the home screen so that opening or completing a
      programme step can collapse the card, and so it re-opens EXPANDED on each fresh
      app load. */
  collapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  /** True when Calm Your Body is member-gated and the user is not a member — the
      tile becomes an unlock prompt. */
  locked?: boolean;
  /** Trial lapsed (non-member): premium features are locked, so Calm Your Body
      offers ONLY the panic route (grounding stays free for safety); the other
      options prompt to unlock with membership. Distinct from `locked`, which
      hides the whole panel behind the unlock tile. */
  featureLock?: boolean;
  onLocked?: () => void;
}) {
  const progress = useProgress();
  // The arrival choice opens EXPANDED on every fresh app load (the home screen owns
  // that and resets it per launch). It collapses to a quiet "How are you arriving
  // today?" re-entry tile after the user uses a tool, taps Close, or starts on the
  // programme. (This is the single arrival check-in — the old "How's today landing?"
  // box was removed.)
  const [step, setStep] = React.useState<Step>(() => {
    const resume = resumeArrivalStep;
    resumeArrivalStep = null;
    return resume ?? initialStep ?? 'choice';
  });
  const [stateId, setStateId] = React.useState<string | null>(null);
  const [techIndex, setTechIndex] = React.useState(0);
  // Collapse helper: reset the internal flow to the top so the next open starts
  // fresh, then hand the collapse up to the home screen.
  const collapse = React.useCallback(() => { setStep('choice'); onCollapse(); }, [onCollapse]);

  // H3 (really-hard): Craig's warmth fires AFTER the user has set their emotion —
  // on the "what's manageable" step, once they've named the hard feeling. It rides
  // ABOVE the choices and never gates them (the tool is right there). Showing it on
  // the pill-selection screen meant it only flashed as the user tapped through, so
  // it never really landed; the manageable step is where the user pauses. Suppressed
  // only while paused (not by "my own way").
  const guide = useGuide();
  const [h3, setH3] = React.useState<GuideSelection | null>(null);
  React.useEffect(() => {
    if (step === 'manageable' && !newUi) setH3((cur) => cur ?? guide.pick('H3_reallyHard'));
    else setH3(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // New Today context: lift each step to the top of the view as the user advances.
  React.useEffect(() => { onStepChange?.(); }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const startBreathing = (protocolId: string, careEligible?: boolean) => {
    onStartBreathing(protocolId, careEligible);
    collapse(); // after a breathing session, collapse to the re-entry tile
  };

  // ── collapsed: a slim, quiet re-entry tile (shown once a session is done / the
  //     user has moved on, so it sits softly at the top without competing). Member-
  //     gated (locked) users always see this tile — the expanded flow never auto-
  //     opens a feature they can't use. ──
  if (collapsed || locked) {
    // The arrival check-in is the guide (H6) tile: Craig on the left, a warm
    // "how are you arriving today?" and a Check-in affordance on the right. Tapping
    // opens the Calm Your Body choice. (Image is the editable H6 'self-care' clip;
    // transparent PNG so it shows the tile through, object-fit contain so it isn't
    // clipped — matching the guide-presence tiles.)
    return (
      <Pressable
        as="button"
        onClick={() => {
          if (locked) { onLocked?.(); return; }
          progress.markCheckin(); // stamps engagement this session
          setStep('choice');
          onExpand();
        }}
        ariaLabel={locked ? 'Calm Your Body — a member feature, unlock to use it' : 'Check in — how are you arriving today?'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          border: '1px solid var(--hair)',
          borderRadius: 18,
          overflow: 'hidden',
          background: 'var(--card-raised-grad)',
          boxShadow: 'var(--edge-light), var(--lift-soft)',
        }}
      >
        <img
          src={clipUrl(GUIDE_MESSAGES.H6_checkinInvite.clipId)}
          alt=""
          aria-hidden="true"
          style={{
            flexShrink: 0,
            width: 88,
            height: 104,
            alignSelf: 'center',
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
            opacity: locked ? 0.6 : 1,
          }}
        />
        <span style={{ flex: 1, minWidth: 0, padding: '14px 14px 13px' }}>
          <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, lineHeight: 1.35, color: 'var(--ink)' }}>
            {locked ? 'Calm Your Body' : 'How are you arriving today?'}
          </span>
          {!locked && (
            <span style={{ display: 'block', marginTop: 5, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, lineHeight: 1.4, color: 'var(--ink-soft)' }}>
              Tap here to calm your body, practice relaxation, or address any risks that have appeared.
            </span>
          )}
          {locked ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, color: 'var(--amber-deep)' }}>
              <Icon name="lock" size={13} />
              <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 700 }}>Members · Unlock</span>
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, color: 'var(--green-deep)' }}>
              <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 700 }}>Check in</span>
              <Icon name="chevRight" size={14} />
            </span>
          )}
        </span>
      </Pressable>
    );
  }

  // ── choice (feature-locked: panic route only) ───────────────────────────────
  // After the trial lapses, Calm Your Body offers ONLY the panic reset — a quick
  // grounding exercise, which stays free for safety. Relaxation, the full emotions
  // check-in and the ex tools are membership features, shown as a single unlock
  // prompt. The "I am feeling at risk" safety row is always present.
  if (step === 'choice' && featureLock) {
    return (
      <Tile>
        <Eyebrow>Right now</Eyebrow>
        <Display parts={{ pre: 'Settle ', em: 'panic', post: ' right now.' }} size={21} lh={1.15} />
        <Body dim size={13} style={{ margin: '7px 0 14px' }}>
          A grounding reset for panic is always here for you. The other calming tools are part of membership.
        </Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <OptionRow icon="emoPanic" title="Settle panic" sub="A quick grounding reset" onClick={() => { progress.markCheckin(); setStateId('panic'); setTechIndex(0); setStep('quickReset'); }} />
          <OptionRow icon="lock" title="More calming tools" sub="Relaxation and the full check-in — unlock with membership" onClick={() => onLocked?.()} />
        </div>
        {/* immediate help — the same safety row as the open choice step */}
        <Pressable
          as="button"
          onClick={() => { progress.markCheckin(); onOpenCrisis(); }}
          ariaLabel="I am feeling at risk"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            textAlign: 'left',
            marginTop: 12,
            padding: '13px 14px',
            borderRadius: 14,
            background: 'rgba(161,75,64,0.08)',
            border: '1px solid rgba(161,75,64,0.22)',
            boxShadow: 'var(--lift-soft)',
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true" style={{ display: 'flex', flexShrink: 0, color: '#9C3D2C' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
              <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.4" />
              <path d="M5.6 5.6l3.8 3.8M14.6 14.6l3.8 3.8M18.4 5.6l-3.8 3.8M9.4 14.6l-3.8 3.8" />
            </svg>
          </span>
          <span style={{ flex: 1, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, fontWeight: 600, color: '#9C3D2C' }}>
            I am feeling at risk
          </span>
          <span style={{ color: 'rgba(161,75,64,0.5)', flexShrink: 0, display: 'flex' }}>
            <Icon name="chevRight" size={15} />
          </span>
        </Pressable>
      </Tile>
    );
  }

  // ── choice ────────────────────────────────────────────────────────────────
  if (step === 'choice') {
    return (
      <Tile>
        <Eyebrow>Right now</Eyebrow>
        <Display parts={{ pre: 'What do you ', em: 'need most', post: ' right now?' }} size={21} lh={1.15} />
        <Body dim size={13} style={{ margin: '7px 0 14px' }}>
          However you’ve arrived, start wherever you need to.
        </Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <OptionRow icon="leaf" title="Settle my emotions" sub="Something feels difficult right now" onClick={() => { progress.markCheckin(); setStep('emotions'); }} />
          {/* Placeholder branch — the full "Difficulty with my ex" flow is still to be
              designed; this opens a brief "coming soon" step rather than dead-ending. */}
          <OptionRow icon="shield" title="Handle a moment with my ex" sub="A message, a handover, or the urge to respond" onClick={() => { progress.markCheckin(); setStep('exHub'); }} />
          <OptionRow icon="moon" title="Practice relaxation" sub="Wind down with calm breathing" onClick={() => { progress.markCheckin(); startBreathing(RELAX_PROTOCOL); }} />
        </div>
        {/* immediate help — a full-width, easy-to-hit muted-terracotta row */}
        <Pressable
          as="button"
          onClick={() => { progress.markCheckin(); onOpenCrisis(); }}
          ariaLabel="I am feeling at risk"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            textAlign: 'left',
            marginTop: 12,
            padding: '13px 14px',
            borderRadius: 14,
            background: 'rgba(161,75,64,0.08)',
            border: '1px solid rgba(161,75,64,0.22)',
            // safety affordance reads through consistent placement, not extra
            // prominence — terracotta tint + a soft lift, never the reserved glow.
            boxShadow: 'var(--lift-soft)',
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true" style={{ display: 'flex', flexShrink: 0, color: '#9C3D2C' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
              <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.4" />
              <path d="M5.6 5.6l3.8 3.8M14.6 14.6l3.8 3.8M18.4 5.6l-3.8 3.8M9.4 14.6l-3.8 3.8" />
            </svg>
          </span>
          <span style={{ flex: 1, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, fontWeight: 600, color: '#9C3D2C' }}>
            I am feeling at risk
          </span>
          <span style={{ color: 'rgba(161,75,64,0.5)', flexShrink: 0, display: 'flex' }}>
            <Icon name="chevRight" size={15} />
          </span>
        </Pressable>
        {/* Clear, primary route INTO the programme — promoted from a muted footnote
            so the journey below the arrival card isn't missed. Tapping scrolls the
            feed into view; the bobbing chevron draws the eye downward. */}
        <style>{`@keyframes rnBob{0%,100%{transform:translateY(0)}50%{transform:translateY(4px)}}`}</style>
        <button
          type="button"
          onClick={() => {
            collapse(); // auto-hide the arrival box once the user chooses to continue
            const area = document.querySelector('[data-scroll-area]') as HTMLElement | null;
            if (area) area.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          aria-label="Continue to the programme below"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
            width: '100%',
            marginTop: 18,
            padding: '14px 18px',
            borderRadius: 14,
            border: '1.5px solid var(--amber-deep)',
            background: 'var(--card-grad)',
            boxShadow: 'var(--edge-light), var(--lift-soft)',
            color: 'var(--amber-deep)',
            cursor: 'pointer',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: '0.01em',
          }}
        >
          Continue your programme
          <span aria-hidden="true" style={{ display: 'flex', animation: 'rnBob 1.6s ease-in-out infinite' }}>
            <Icon name="chevDown" size={17} sw={2.4} />
          </span>
        </button>
        {/* Close — collapse to the quiet re-entry tile for the rest of this session */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
          <button
            type="button"
            onClick={collapse}
            aria-label="Close"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-soft)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 12.5,
              fontWeight: 600,
              letterSpacing: '0.02em',
              padding: '8px 10px',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 15l7-7 7 7" />
            </svg>
            Close
          </button>
        </div>
      </Tile>
    );
  }

  // ── difficulty with my ex: tools for contact & boundaries + reading themes ────
  if (step === 'exHub') {
    // 'Hold' is the Boundaries library; 'Respond' is the Co-Parenting & Conflict
    // library (both appear in the Library grid). These keys drive entriesForTag and
    // the LibraryScreen deep-link.
    const boundaries = entriesForTag('Hold');
    const coparenting = entriesForTag('Respond');
    return (
      <Tile>
        <Eyebrow onBack={() => setStep('choice')}>Difficulty with your ex</Eyebrow>
        <Display parts={{ pre: 'What do you ', em: 'need help', post: ' with?' }} size={21} lh={1.15} />
        <Body dim size={13} style={{ margin: '7px 0 16px' }}>
          Tools for contact and boundaries, and things to read when you’re ready.
        </Body>

        {/* steady-first guidance: an offer, never a gate */}
        <div
          style={{
            background: 'linear-gradient(160deg, #EAF1E8, #E1EBDD)',
            border: '1px solid #cfe0cc',
            borderRadius: 16,
            padding: 14,
            marginBottom: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span
              aria-hidden="true"
              style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: '#dcebd7', color: '#3e7d5e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icon name="leaf" size={20} />
            </span>
            <div>
              <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 17, fontWeight: 600, color: '#274a37', lineHeight: 1.12 }}>
                Steady yourself first
              </div>
              <div style={{ color: '#46604d', fontSize: 13, marginTop: 5, lineHeight: 1.45, fontFamily: 'Inter, system-ui, sans-serif' }}>
                This kind of work lands best when you’re calm. If you’re feeling unsettled,
                overwhelmed or upset, settle your emotions first. The tools will still be
                here when you’re ready.
              </div>
            </div>
          </div>
          <Pressable
            as="button"
            onClick={() => { progress.markCheckin(); setStep('emotions'); }}
            ariaLabel="Settle your emotions first"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              background: 'var(--green-deep)',
              color: '#fff',
              border: 'none',
              borderRadius: 13,
              padding: 13,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 14.5,
              cursor: 'pointer',
            }}
          >
            <Icon name="leaf" size={17} /> Settle your emotions first
          </Pressable>
        </div>

        {/* Tools — escalation first (the in-the-moment one) */}
        <SectionLabel>Tools</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <OptionRow icon="emoEdgy" title="Deal with escalation" tag="In the moment" sub="Steady yourself when things heat up" onClick={() => onOpenTool('escalation')} />
          <OptionRow icon="lock" title="Reduce contact" sub="Create distance and limit how they reach you" onClick={() => onOpenTool('beaturge')} />
          <OptionRow icon="shield" title="Build a boundary" sub="Decide what you will and won’t accept" onClick={() => onOpenTool('boundary')} />
          <OptionRow icon="voice" title="Communicate a boundary" sub="Say it clearly, without over-explaining" onClick={() => onOpenTool('communicate')} />
        </div>

        {/* Read & listen — reading themes in the Library */}
        <SectionLabel>Read &amp; listen</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <LibRow title="Boundaries" count={boundaries.length} accent="#3E8E72" onClick={() => { resumeArrivalStep = 'exHub'; onOpenLibraryTheme('Hold'); }} />
          <LibRow title="Co-parenting" count={coparenting.length} accent="#B06A4A" onClick={() => { resumeArrivalStep = 'exHub'; onOpenLibraryTheme('Respond'); }} />
        </div>

        {/* immediate help — the same full-width muted-terracotta row as the choice step */}
        <Pressable
          as="button"
          onClick={() => { progress.markCheckin(); onOpenCrisis(); }}
          ariaLabel="I am feeling at risk"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            textAlign: 'left',
            marginTop: 14,
            padding: '13px 14px',
            borderRadius: 14,
            background: 'rgba(161,75,64,0.08)',
            border: '1px solid rgba(161,75,64,0.22)',
            // terracotta tint + a soft lift, never the reserved glow — see choice step.
            boxShadow: 'var(--lift-soft)',
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true" style={{ display: 'flex', flexShrink: 0, color: '#9C3D2C' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
              <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.4" />
              <path d="M5.6 5.6l3.8 3.8M14.6 14.6l3.8 3.8M18.4 5.6l-3.8 3.8M9.4 14.6l-3.8 3.8" />
            </svg>
          </span>
          <span style={{ flex: 1, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, fontWeight: 600, color: '#9C3D2C' }}>
            I am feeling at risk
          </span>
          <span style={{ color: 'rgba(161,75,64,0.5)', flexShrink: 0, display: 'flex' }}>
            <Icon name="chevRight" size={15} />
          </span>
        </Pressable>
      </Tile>
    );
  }

  // ── emotions ──────────────────────────────────────────────────────────────
  if (step === 'emotions') {
    // Legacy: the original inline pill/grid card, byte-identical to before the
    // full-screen redesign (dead now that CALM_FULLSCREEN is always true, kept for now).
    if (!CALM_FULLSCREEN) {
      return (
        <Tile tone="calm">
          <Eyebrow onBack={() => { if (newUi) onCollapse(); else setStep('choice'); }}>Settle your emotions</Eyebrow>
          <Display parts={{ pre: 'How are you ', em: 'feeling', post: '?' }} size={20} lh={1.15} />
          <Body dim size={13} style={{ margin: '7px 0 14px' }}>
            Tap what fits closest. We’ve built support to soothe each of these states, so you’ll get help shaped to how you actually feel.
          </Body>
          <div style={newUi ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } : { display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {EMOTION_STATES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setStateId(s.id);
                  setTechIndex(0);
                  setStep('manageable');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 9,
                  padding: newUi ? '13px 13px' : '9px 14px',
                  borderRadius: newUi ? 12 : 999,
                  border: '1px solid var(--hair)',
                  background: 'var(--card-top)',
                  color: 'var(--ink)',
                  cursor: 'pointer',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 13.5,
                  fontWeight: 600,
                  width: newUi ? '100%' : undefined,
                  minHeight: newUi ? 50 : undefined,
                }}
              >
                <span aria-hidden="true" style={{ color: 'var(--amber-deep)', display: 'flex', flexShrink: 0 }}>
                  <Icon name={STATE_ICON[s.id] ?? 'sparkle'} size={16} />
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {newUi && s.id === 'edgy' ? 'Stressed' : (STATE_LABEL[s.id] ?? shortLabel(s.label))}
                </span>
              </button>
            ))}
          </div>
        </Tile>
      );
    }
    // Full screen — every state shown at once, no swiping.
    return (
      <FullScreenShell
        title="Settle your emotions"
        onBack={() => { if (newUi) onCollapse(); else setStep('choice'); }}
        onClose={collapse}
        standardBack={standardBack}
      >
        <Display parts={{ pre: 'How are you ', em: 'feeling', post: '?' }} size={24} lh={1.15} style={{ marginTop: 12 }} />
        <Body dim size={14} style={{ margin: '10px 0 18px' }}>
          Tap what fits closest. We’ve built support to soothe each of these states, so you’ll get help shaped to how you actually feel.
        </Body>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {EMOTION_STATES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setStateId(s.id);
                setTechIndex(0);
                setStep('manageable');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 9,
                padding: '14px 13px',
                borderRadius: 12,
                border: '1px solid var(--hair)',
                background: 'var(--card-top)',
                color: 'var(--ink)',
                cursor: 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 13.5,
                fontWeight: 600,
                width: '100%',
                minHeight: 54,
              }}
            >
              <span aria-hidden="true" style={{ color: 'var(--amber-deep)', display: 'flex', flexShrink: 0 }}>
                <Icon name={STATE_ICON[s.id] ?? 'sparkle'} size={16} />
              </span>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {/* Native always renders the tighter grid layout above (no pill/grid
                    split like the web path), so the shortened "Stressed" label — on
                    web gated on newUi — applies unconditionally here too. */}
                {s.id === 'edgy' ? 'Stressed' : (STATE_LABEL[s.id] ?? shortLabel(s.label))}
              </span>
            </button>
          ))}
        </div>
      </FullScreenShell>
    );
  }

  // ── manageable ────────────────────────────────────────────────────────────
  if (step === 'manageable') {
    // Legacy: the original inline card (H3 guide rides above the Tile).
    if (!CALM_FULLSCREEN) {
      return (
        <>
          {/* H3 — Craig's hard-day warmth, fired once the feeling is named. It
              accompanies (never gates) the next choice. */}
          {h3 && (
            <div style={{ marginBottom: 12 }}>
              <GuidePresence selection={h3} onShown={() => guide.record(h3)} onClose={() => setH3(null)} />
            </div>
          )}
          <Tile tone="calm">
            <Eyebrow onBack={() => setStep('emotions')}>What’s manageable right now?</Eyebrow>
            <Display parts={{ pre: 'A little, or a bit ', em: 'deeper', post: '.' }} size={20} lh={1.15} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 14 }}>
              <OptionRow
                icon="sparkle"
                title="A quick reset"
                sub="A short grounding exercise"
                onClick={() => {
                  setTechIndex(0);
                  setStep('quickReset');
                }}
              />
              <OptionRow
                icon="moon"
                tone="green"
                title="A breathing exercise"
                sub="A guided breathing session"
                onClick={() => {
                  progress.triggerCare();
                  startBreathing(protocolForState(stateId ?? '')?.id ?? RELAX_PROTOCOL, true);
                }}
              />
            </div>
          </Tile>
        </>
      );
    }
    // Full screen.
    return (
      <FullScreenShell title="What’s manageable right now?" onBack={() => setStep('emotions')} onClose={collapse} standardBack={standardBack}>
        {/* H3 — Craig's hard-day warmth, fired once the feeling is named. It
            accompanies (never gates) the next choice. */}
        {h3 && (
          <div style={{ marginTop: 12, marginBottom: 4 }}>
            <GuidePresence selection={h3} onShown={() => guide.record(h3)} onClose={() => setH3(null)} />
          </div>
        )}
        <Display parts={{ pre: 'A little, or a bit ', em: 'deeper', post: '.' }} size={24} lh={1.15} style={{ marginTop: 12 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 18 }}>
          <OptionRow
            icon="sparkle"
            title="A quick reset"
            sub="A short grounding exercise"
            onClick={() => {
              setTechIndex(0);
              setStep('quickReset');
            }}
          />
          <OptionRow
            icon="moon"
            tone="green"
            title="A breathing exercise"
            sub="A guided breathing session"
            onClick={() => {
              progress.triggerCare();
              startBreathing(protocolForState(stateId ?? '')?.id ?? RELAX_PROTOCOL, true);
            }}
          />
        </div>
      </FullScreenShell>
    );
  }

  // ── quick reset (technique tiles) ────────────────────────────────────────────
  if (step === 'quickReset') {
    const techniques: Technique[] = stateId ? techniquesForState(stateId) : [];
    const tech = techniques.length ? techniques[techIndex % techniques.length] : null;
    return (
      <Tile>
        <Eyebrow onBack={() => setStep('manageable')}>Quick reset</Eyebrow>
        {tech ? (
          <>
            {/* full-width image band — bleeds to the tile's side edges */}
            <img
              src={tech.image}
              alt=""
              aria-hidden="true"
              style={{
                display: 'block',
                width: 'calc(100% + 32px)',
                marginLeft: -16,
                marginRight: -16,
                marginBottom: 13,
                height: 170,
                objectFit: 'cover',
                background: 'var(--paper)',
              }}
            />
            <Display parts={{ pre: tech.title, em: '' }} size={19} lh={1.15} />
            <Body dim size={13} style={{ marginTop: 3, marginBottom: 12 }}>
              {tech.desc}
            </Body>
            <ol style={{ margin: '0 0 12px', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tech.steps.map((s, i) => (
                <li key={i} style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, lineHeight: 1.5, color: 'var(--ink)' }}>
                  {s}
                </li>
              ))}
            </ol>
            {tech.guidance && (
              <Body dim size={13} style={{ fontStyle: 'italic', marginBottom: 14 }}>
                {tech.guidance}
              </Body>
            )}
            <div style={{ display: 'flex', gap: 9 }}>
              {techniques.length > 1 && (
                <Pressable
                  as="button"
                  onClick={() => setTechIndex((i) => i + 1)}
                  ariaLabel="Show another reset"
                  style={{
                    flex: 1,
                    padding: '11px 14px',
                    borderRadius: 13,
                    border: '1px solid var(--hair)',
                    background: 'var(--card-top)',
                    color: 'var(--ink)',
                    cursor: 'pointer',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 13.5,
                    fontWeight: 600,
                  }}
                >
                  Show another
                </Pressable>
              )}
              <Pressable
                as="button"
                onClick={() => {
                  progress.triggerCare();
                  setStep('quickResetDone');
                }}
                ariaLabel="I'm done"
                style={{
                  flex: 1,
                  padding: '11px 14px',
                  borderRadius: 13,
                  border: 'none',
                  background: 'var(--green-deep)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 13.5,
                  fontWeight: 600,
                }}
              >
                I’m done
              </Pressable>
            </div>
          </>
        ) : (
          <Body dim size={13}>
            No quick reset available for this — try a breathing exercise instead.
          </Body>
        )}
      </Tile>
    );
  }

  // ── quick reset done: offer CARE as the next step (additive, never a gate) ──
  if (step === 'quickResetDone') {
    return (
      <Tile>
        <Display parts={{ pre: 'Do ', em: 'CARE', post: ' next?' }} size={19} lh={1.15} />
        <Body dim size={13} style={{ marginTop: 8, marginBottom: 16 }}>
          {CARE_TAGLINE}
        </Body>
        <div style={{ display: 'flex', gap: 9 }}>
          <Pressable
            as="button"
            onClick={collapse}
            ariaLabel="Not now"
            style={{
              flex: 1,
              padding: '11px 14px',
              borderRadius: 13,
              border: '1px solid var(--hair)',
              background: 'var(--card-top)',
              color: 'var(--ink)',
              cursor: 'pointer',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            Not now
          </Pressable>
          <Pressable
            as="button"
            onClick={() => { collapse(); onOpenCare?.(); }}
            ariaLabel="Start CARE"
            style={{
              flex: 1,
              padding: '11px 14px',
              borderRadius: 13,
              border: 'none',
              background: 'var(--green-deep)',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            Start CARE
          </Pressable>
        </div>
      </Tile>
    );
  }

  return null;
}
